import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash2, CheckCircle2, XCircle, Wifi, Power, Zap, Activity, KeyRound, Layers, AlertTriangle, Sparkles, Wallet, ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Textarea } from "@/components/ui/Textarea.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { useToast } from "@/stores/toastStore.ts";
import {
  adminApi,
  type ProviderConfig,
  type ProviderCatalogEntry,
  type ProviderBalance,
} from "@/api/admin.ts";
import type { ApiError } from "@/api/client.ts";

type Tab = "credentials" | "configs" | "health" | "balances";

export function AdminProvidersPage() {
  const [tab, setTab] = useState<Tab>("credentials");

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-fg">Providers</h1>
        <p className="mt-1 text-fgMuted">
          Manage video/image provider credentials, per-tier models, priority, and fallback.
        </p>
      </div>

      <div className="mb-6 flex gap-2">
        <TabButton active={tab === "credentials"} onClick={() => setTab("credentials")} icon={<KeyRound className="h-4 w-4" />}>
          Credentials
        </TabButton>
        <TabButton active={tab === "configs"} onClick={() => setTab("configs")} icon={<Layers className="h-4 w-4" />}>
          Providers &amp; Models
        </TabButton>
        <TabButton active={tab === "health"} onClick={() => setTab("health")} icon={<Activity className="h-4 w-4" />}>
          Health
        </TabButton>
        <TabButton active={tab === "balances"} onClick={() => setTab("balances")} icon={<Wallet className="h-4 w-4" />}>
          Balances & Spend
        </TabButton>
      </div>

      {tab === "credentials" && <CredentialsTab />}
      {tab === "configs" && <ConfigsTab />}
      {tab === "health" && <HealthTab />}
      {tab === "balances" && <BalancesTab />}
    </div>
  );
}

function TabButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
        active ? "bg-surface2 text-fg" : "text-fgMuted hover:text-fg"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

// ── Credentials tab: one entry per provider ────────────────────────────────
function CredentialsTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<ProviderCatalogEntry | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; msg: string; kind: "health" | "generation" }>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "provider-credentials"],
    queryFn: () => adminApi.listProviderCredentials(),
  });

  const test = useMutation({
    mutationFn: (moduleId: string) => adminApi.testProviderCredentials(moduleId),
    onSuccess: (r) => setTestResult((prev) => ({ ...prev, [r.moduleId]: { ok: r.success, msg: r.message, kind: "health" } })),
    onError: (e: ApiError, moduleId) => setTestResult((prev) => ({ ...prev, [moduleId]: { ok: false, msg: e.message || "Health check failed", kind: "health" } })),
  });
  const testGen = useMutation({
    mutationFn: (moduleId: string) => adminApi.testProviderGeneration(moduleId),
    onSuccess: (r) => {
      setTestResult((prev) => ({ ...prev, [r.moduleId]: { ok: r.success, msg: r.message, kind: "generation" } }));
      toast.info("Real generation queued — check the Health tab for the result.");
    },
    onError: (e: ApiError, moduleId) => setTestResult((prev) => ({ ...prev, [moduleId]: { ok: false, msg: e.message || "Test generation failed to queue", kind: "generation" } })),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const catalog = data?.catalog ?? [];
  const stored = new Map((data?.credentials ?? []).map((c) => [c.moduleId, c]));

  return (
    <div className="flex flex-col gap-4">
      {catalog.map((prov) => {
        const cred = stored.get(prov.id);
        const result = testResult[prov.id];
        return (
          <Card key={prov.id}>
            <CardContent className="flex items-center gap-4 py-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-surface2 text-lg font-bold text-fgMuted uppercase">
                {prov.id.slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-fg">{prov.displayName}</p>
                  <Badge variant={prov.kind === "video" ? "info" : "muted"}>{prov.kind}</Badge>
                  {cred?.undecryptable?.length
                    ? <Badge variant="danger">Couldn't decrypt</Badge>
                    : <Badge variant={cred ? "success" : "muted"}>{cred ? "Configured" : "Not set"}</Badge>}
                </div>
                {cred && (
                  <p className="mt-0.5 text-sm text-fgMuted">
                    {Object.entries(cred.credentials).map(([k, v]) => `${k}=${v}`).join("  ")}
                  </p>
                )}
                {!!cred?.undecryptable?.length && (
                  <p className="mt-0.5 text-sm text-danger">
                    {cred.undecryptable.join(", ")} couldn't be decrypted — re-save this provider's credentials (wrong APP_SECRET_KEY, or a corrupt value)
                  </p>
                )}
                {result && (
                  <p className={`mt-1 flex items-center gap-1 text-sm ${result.ok ? "text-success" : "text-danger"}`}>
                    {result.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    <span className="font-medium">{result.kind === "health" ? "Health check:" : "Test generation:"}</span> {result.msg}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => test.mutate(prov.id)} disabled={!cred || test.isPending} title="Cheap connectivity check — no generation cost">
                  <Wifi className="h-4 w-4" /> {test.isPending && test.variables === prov.id ? "Testing…" : "Test"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (confirm(`Run a real ${prov.kind} generation with ${prov.displayName}? This incurs real provider cost. The result appears in the Health tab.`)) {
                      testGen.mutate(prov.id);
                    }
                  }}
                  disabled={!cred || testGen.isPending}
                  title="Runs a real generation — incurs provider cost"
                >
                  <Zap className="h-4 w-4" /> Test generation
                </Button>
                <Button onClick={() => setEditing(prov)}>{cred ? "Edit" : "Set keys"}</Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {editing && (
        <CredentialModal
          provider={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ["admin", "provider-credentials"] });
          }}
        />
      )}
    </div>
  );
}

function CredentialModal({ provider, onClose, onSaved }: { provider: ProviderCatalogEntry; onClose: () => void; onSaved: () => void }) {
  const allKeys = [...provider.requiredCredentials, ...(provider.optionalCredentials ?? [])];
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => adminApi.upsertProviderCredentials(provider.id, values),
    onSuccess: onSaved,
    onError: (e: ApiError) => setError(e.message),
  });

  return (
    <Modal open onClose={onClose} title={`${provider.displayName} credentials`}>
      <div className="flex flex-col gap-4">
        {allKeys.map((key) => (
          <div key={key}>
            <label className="mb-1 block text-sm font-medium text-fg">
              {key}
              {provider.requiredCredentials.includes(key) && <span className="text-danger"> *</span>}
            </label>
            <Input
              type={key.toLowerCase().includes("secret") || key.toLowerCase().includes("key") ? "password" : "text"}
              value={values[key] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
              placeholder={provider.requiredCredentials.includes(key) ? "required" : "optional"}
            />
          </div>
        ))}
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Configs tab: per (provider, tier, model) rows ──────────────────────────
function ConfigsTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  // The image-kind config row whose SPARK image settings are being edited, if any.
  const [imageSettingsFor, setImageSettingsFor] = useState<ProviderConfig | null>(null);
  const [capabilitiesFor, setCapabilitiesFor] = useState<ProviderConfig | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "providers"],
    queryFn: () => adminApi.listProviders(),
  });

  // Which modules have credentials — used to warn before activating a row that
  // can't actually run (the resolver would silently skip it).
  const { data: credData } = useQuery({
    queryKey: ["admin", "provider-credentials"],
    queryFn: () => adminApi.listProviderCredentials(),
  });
  const configuredModules = new Set((credData?.credentials ?? []).map((c) => c.moduleId));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "providers"] });
  const onErr = (e: ApiError) => toast.error(e.message || "Update failed");

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => adminApi.updateProvider(id, { isActive }),
    onSuccess: (_r, v) => { invalidate(); toast.success(v.isActive ? "Provider activated" : "Provider deactivated"); },
    onError: onErr,
  });
  const setPriority = useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: number }) => adminApi.updateProvider(id, { priority }),
    onSuccess: () => { invalidate(); toast.success("Priority updated"); },
    onError: onErr,
  });
  const toggleFailover = useMutation({
    mutationFn: ({ id, failoverEnabled }: { id: string; failoverEnabled: boolean }) => adminApi.updateProvider(id, { failoverEnabled }),
    onSuccess: () => { invalidate(); toast.success("Failover setting updated"); },
    onError: onErr,
  });
  const del = useMutation({
    mutationFn: (id: string) => adminApi.deleteProvider(id),
    onSuccess: () => { invalidate(); toast.success("Provider row deleted"); },
    onError: onErr,
  });

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const configs = data?.configs ?? [];
  const catalog = data?.catalog ?? [];

  // Group by kind+tier to show the fallback chain order.
  const groups: Record<string, ProviderConfig[]> = {};
  for (const c of configs) {
    (groups[`${c.kind} · ${c.tier}`] ??= []).push(c);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Button onClick={() => setShowAdd(true)}><Plus className="h-4 w-4" /> Add provider row</Button>
      </div>

      {Object.keys(groups).length === 0 ? (
        <Card><CardContent className="py-16 text-center text-fgMuted">
          <p className="mb-1 font-medium text-fg">No provider rows yet</p>
          <p className="text-sm">First set a provider's keys in the <strong>Credentials</strong> tab, then add a row here (choose provider, tier, and model), and finally activate it. The topmost active row per tier is preferred; the rest form the fallback chain.</p>
        </CardContent></Card>
      ) : (
        Object.entries(groups).map(([group, rows]) => (
          <Card key={group}>
            <CardHeader>
              <CardTitle className="capitalize text-base">
                {group}
                <span className="ml-2 text-sm font-normal text-fgMuted">
                  fallback order (top = preferred)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {rows.map((c, i) => {
                const hasCreds = configuredModules.has(c.moduleId);
                return (
                <div key={c.id} className={`flex items-center gap-3 rounded-lg border border-border p-3 ${c.isActive ? "" : "opacity-50"}`}>
                  <span className="w-6 text-center text-sm font-mono text-fgMuted">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-fg">{c.label}</p>
                      <Badge variant="muted">{c.moduleId}</Badge>
                      {c.isLastResort && <Badge variant="warning">last resort</Badge>}
                      {!c.failoverEnabled && <Badge variant="danger">pinned</Badge>}
                      {c.kind === "image" && (c.renderClasses?.length || c.defaultNegatives?.length || (c.renderParams && Object.keys(c.renderParams).length)) ? (
                        <Badge variant="muted" title="Has SPARK image overrides (render classes / negatives / params)">
                          <Sparkles className="mr-1 h-3 w-3" />SPARK
                        </Badge>
                      ) : null}
                      {!hasCreds && (
                        <Badge variant="danger" title="No credentials set for this provider — it will be skipped at generation time. Add keys in the Credentials tab.">
                          <AlertTriangle className="mr-1 h-3 w-3" />no credentials
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-sm text-fgMuted">
                      model: <span className="font-mono">{c.modelSlug}</span> · {c.costPerGeneration}¢ / generation · retries {c.maxRetries}
                    </p>
                  </div>
                  <Input
                    type="number"
                    className="w-20"
                    defaultValue={c.priority}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v !== c.priority) setPriority.mutate({ id: c.id, priority: v });
                    }}
                    title="priority (lower = preferred)"
                  />
                  <Button variant="secondary" onClick={() => toggleFailover.mutate({ id: c.id, failoverEnabled: !c.failoverEnabled })} title="toggle failover">
                    {c.failoverEnabled ? "Failover on" : "Failover off"}
                  </Button>
                  {c.kind === "image" && (
                    <Button variant="secondary" onClick={() => setImageSettingsFor(c)} title="SPARK image-gen settings (render classes, negatives, params)">
                      <Sparkles className="h-4 w-4" /> Image settings
                    </Button>
                  )}
                  <Button variant="secondary" onClick={() => setCapabilitiesFor(c)} title="Provider capability flags (lip-sync, audio, reference image, presenter avatar…)">
                    <Layers className="h-4 w-4" /> Capabilities
                  </Button>
                  <Button
                    variant={c.isActive ? "secondary" : "primary"}
                    onClick={() => {
                      // Warn before activating a row that can't run (no credentials).
                      if (!c.isActive && !hasCreds &&
                        !confirm(`"${c.moduleId}" has no credentials set, so it will be skipped at generation time. Activate anyway?`)) {
                        return;
                      }
                      toggleActive.mutate({ id: c.id, isActive: !c.isActive });
                    }}
                  >
                    <Power className="h-4 w-4" /> {c.isActive ? "Active" : "Inactive"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { if (confirm(`Delete provider row "${c.label}" (${c.moduleId} · ${c.tier})? This cannot be undone.`)) del.mutate(c.id); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                );
              })}
            </CardContent>
          </Card>
        ))
      )}

      {showAdd && (
        <AddConfigModal
          catalog={catalog}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            queryClient.invalidateQueries({ queryKey: ["admin", "providers"] });
          }}
        />
      )}

      {imageSettingsFor && (
        <ImageSettingsModal
          config={imageSettingsFor}
          onClose={() => setImageSettingsFor(null)}
          onSaved={() => {
            setImageSettingsFor(null);            queryClient.invalidateQueries({ queryKey: ["admin", "providers"] });
          }}
        />
      )}

      {capabilitiesFor && (
        <CapabilitiesModal
          config={capabilitiesFor}
          onClose={() => setCapabilitiesFor(null)}
          onSaved={() => {
            setCapabilitiesFor(null);
            queryClient.invalidateQueries({ queryKey: ["admin", "providers"] });
          }}
        />
      )}
    </div>
  );
}

function AddConfigModal({ catalog, onClose, onSaved }: { catalog: ProviderCatalogEntry[]; onClose: () => void; onSaved: () => void }) {
  const [moduleId, setModuleId] = useState(catalog[0]?.id ?? "");
  const [tier, setTier] = useState<"preview" | "final">("preview");
  const [label, setLabel] = useState("");
  const [modelSlug, setModelSlug] = useState("");
  const [priority, setPriority] = useState(100);
  const [cost, setCost] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const entry = catalog.find((c) => c.id === moduleId);
  const suggestedModel = tier === "preview" ? entry?.defaultModels?.preview : entry?.defaultModels?.final;

  const create = useMutation({
    mutationFn: () =>
      adminApi.createProvider({
        moduleId,
        label: label || `${moduleId} ${tier}`,
        kind: entry?.kind ?? "video",
        tier,
        modelSlug: modelSlug || suggestedModel || "",
        priority,
        costPerGeneration: cost,
        isActive: false,
      }),
    onSuccess: onSaved,
    onError: (e: ApiError) => setError(e.message),
  });

  return (
    <Modal open onClose={onClose} title="Add provider row">
      <div className="flex flex-col gap-4">
        <Select
          label="Provider"
          value={moduleId}
          onChange={(e) => setModuleId(e.target.value)}
          options={catalog.map((c) => ({ value: c.id, label: `${c.displayName} (${c.kind})` }))}
        />
        <Select
          label="Tier"
          value={tier}
          onChange={(e) => setTier(e.target.value as "preview" | "final")}
          options={[
            { value: "preview", label: "preview" },
            { value: "final", label: "final" },
          ]}
        />
        <div>
          <label className="mb-1 block text-sm font-medium text-fg">Label</label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`${moduleId} ${tier}`} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-fg">Model slug</label>
          <Input value={modelSlug} onChange={(e) => setModelSlug(e.target.value)} placeholder={suggestedModel ?? "model id"} />
          {suggestedModel && !modelSlug && (
            <p className="mt-1 text-xs text-fgMuted">Leave blank to use the suggested default: <span className="font-mono">{suggestedModel}</span></p>
          )}
          {!suggestedModel && !modelSlug && (
            <p className="mt-1 text-xs text-danger">This provider has no default for this tier — enter a model slug.</p>
          )}
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium text-fg">Priority</label>
            <Input type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
          </div>
          <div className="flex-1">
            {/* Session 3 F (P2): cost_per_generation is US CENTS per call — the workers charge it to the run's budget and the
                backend's spend rollup reads it as cents. It was labelled "credits". */}
            <label className="mb-1 block text-sm font-medium text-fg">Cost per generation (US cents — charged to the run's budget)</label>
            <Input type="number" value={cost} onChange={(e) => setCost(Number(e.target.value))} />
          </div>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !(modelSlug || suggestedModel)}>
            {create.isPending ? "Adding…" : "Add row"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── SPARK image-gen settings for an image-kind config row ──────────────────
// Edits the three nullable overrides the engine reads per model:
//   · renderClasses    — content classes this provider is good at; the resolver
//                        promotes matching providers to the head of the chain.
//   · defaultNegatives — baseline negative-prompt terms merged in by the compiler.
//   · renderParams     — flat scalar params merged as the BASE layer (the engine's
//                        own structural keys always win, so this can't override
//                        geometry or seed).
// Empty inputs send null, which clears the override (feature off, prior behaviour).
// Validation mirrors the backend Zod schema so bad shapes are caught before the
// request rather than silently dropped.
const RENDER_CLASS_OPTIONS = ["photoreal", "graphic", "text_heavy"] as const;

// CapabilitiesModal (item 8) — edit a provider's boolean capability flags via
// CHECKBOXES, never raw JSON. The flags drive provider selection/skip-policy;
// entering them as JSON is how the array-vs-object bug happened. This posts a
// validated flat object; the backend's strict zod rejects any other shape.
const CAPABILITY_FLAGS: { key: string; label: string; hint: string }[] = [
  { key: "supports_lip_sync", label: "Lip sync", hint: "can sync speech to a face (talking-head)" },
  { key: "supports_audio", label: "Audio", hint: "generates or accepts audio" },
  { key: "supports_reference_image", label: "Reference image", hint: "accepts a reference/character image" },
  { key: "presenter_avatar", label: "Presenter avatar", hint: "renders a presenter/avatar (e.g. HeyGen)" },
  { key: "diffusion_motion", label: "Diffusion motion", hint: "generates motion via diffusion (not a presenter)" },
  { key: "image_to_video", label: "Image → video", hint: "animates a still image into video" },
];

function CapabilitiesModal({
  config,
  onClose,
  onSaved,
}: {
  config: ProviderConfig;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [flags, setFlags] = useState<Record<string, boolean>>(
    () => {
      const init: Record<string, boolean> = {};
      const cur = config.capabilities ?? {};
      for (const f of CAPABILITY_FLAGS) init[f.key] = Boolean((cur as Record<string, boolean | undefined>)[f.key]);
      return init;
    },
  );
  const [error, setError] = useState<string | null>(null);

  const toggle = (key: string) => setFlags((prev) => ({ ...prev, [key]: !prev[key] }));

  const save = useMutation({
    // Send only the flags that are ON as an object of booleans — the strict shape
    // the backend validates. (Off flags are sent false so a cleared flag persists.)
    mutationFn: () => adminApi.updateProvider(config.id, { capabilities: flags }),
    onSuccess: () => { toast.success("Capabilities saved"); onSaved(); },
    onError: (e: ApiError) => setError(e.message || "Save failed"),
  });

  return (
    <Modal open onClose={onClose} title={`Capabilities — ${config.label}`} size="md">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fgMuted">
          The provider's feature flags that drive selection. Checkboxes only — never raw JSON,
          so the shape is always correct.
        </p>
        <div className="flex flex-col gap-2">
          {CAPABILITY_FLAGS.map((f) => (
            <label key={f.key} className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(flags[f.key])}
                onChange={() => toggle(f.key)}
                className="mt-0.5"
              />
              <span>
                <span className="text-sm font-medium text-fg">{f.label}</span>
                <span className="ml-2 font-mono text-xs text-fgMuted">{f.key}</span>
                <span className="block text-xs text-fgMuted">{f.hint}</span>
              </span>
            </label>
          ))}
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { setError(null); save.mutate(); }} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save capabilities"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ImageSettingsModal({
  config,
  onClose,
  onSaved,
}: {
  config: ProviderConfig;
  onClose: () => void;
  onSaved: () => void;
}) {  const toast = useToast();
  const [classes, setClasses] = useState<Set<string>>(
    () => new Set(config.renderClasses ?? []),
  );
  // Negatives: one term per line — the natural shape for a string[] the admin edits.
  const [negativesText, setNegativesText] = useState(
    () => (config.defaultNegatives ?? []).join("\n"),
  );
  // Params: JSON object of scalar values. A textarea keeps arbitrary keys open
  // without inventing a key/value row UI the backend doesn't need.
  const [paramsText, setParamsText] = useState(() =>
    config.renderParams && Object.keys(config.renderParams).length > 0
      ? JSON.stringify(config.renderParams, null, 2)
      : "",
  );
  const [error, setError] = useState<string | null>(null);

  const toggleClass = (c: string) => {
    setClasses((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  /** Build the PATCH body, or throw a human message if a field is malformed. */
  function buildBody(): Record<string, unknown> {
    // renderClasses — empty selection clears it.
    const renderClasses = classes.size > 0 ? [...classes] : null;

    // defaultNegatives — split on newlines, trim, drop blanks. Empty ⇒ null.
    const negatives = negativesText
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    for (const n of negatives) {
      if (n.length > 200) throw new Error(`Negative term too long (max 200 chars): "${n.slice(0, 30)}…"`);
    }
    if (negatives.length > 50) throw new Error("Too many negative terms (max 50).");
    const defaultNegatives = negatives.length > 0 ? negatives : null;

    // renderParams — parse JSON; must be a flat object of scalar values, matching
    // what the engine's coerceRenderParams keeps. Empty ⇒ null.
    let renderParams: Record<string, string | number | boolean> | null = null;
    const trimmed = paramsText.trim();
    if (trimmed.length > 0) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        throw new Error("Render params must be valid JSON (an object like {\"guidance\": 3.5}).");
      }
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("Render params must be a JSON object, not an array or primitive.");
      }
      const out: Record<string, string | number | boolean> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v !== "string" && typeof v !== "number" && typeof v !== "boolean") {
          throw new Error(`Render param "${k}" must be a string, number, or boolean — nested objects and arrays are not allowed.`);
        }
        out[k] = v;
      }
      renderParams = Object.keys(out).length > 0 ? out : null;
    }

    return { renderClasses, defaultNegatives, renderParams };
  }

  const save = useMutation({
    mutationFn: () => adminApi.updateProvider(config.id, buildBody()),
    onSuccess: () => {
      toast.success("Image settings saved");
      onSaved();
    },
    onError: (e: ApiError) => setError(e.message || "Save failed"),
  });

  const onSaveClick = () => {
    setError(null);
    try {
      buildBody(); // validate before firing so errors show inline, not as a 400
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    save.mutate();
  };

  return (
    <Modal open onClose={onClose} title={`Image settings — ${config.label}`} size="lg">
      <div className="flex flex-col gap-5">
        <p className="text-sm text-fgMuted">
          Per-model SPARK overrides for <span className="font-mono">{config.modelSlug}</span> ({config.tier}).
          Leave a field empty to clear it — the model then behaves with engine defaults.
        </p>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-fg">Render classes</label>
          <p className="mb-2 text-xs text-fgMuted">
            Content this provider is good at. When a request wants one of these, matching providers are moved to the front of the fallback chain.
          </p>
          <div className="flex flex-wrap gap-2">
            {RENDER_CLASS_OPTIONS.map((c) => {
              const on = classes.has(c);
              return (
                <Button
                  key={c}
                  type="button"
                  variant={on ? "primary" : "secondary"}
                  onClick={() => toggleClass(c)}
                >
                  {on && <CheckCircle2 className="h-4 w-4" />} {c}
                </Button>
              );
            })}
          </div>
        </div>

        <Textarea
          label="Default negatives"
          hint="One term per line. Merged into the negative prompt for this model. Only applies if the model supports a negative prompt."
          value={negativesText}
          onChange={(e) => setNegativesText(e.target.value)}
          rows={5}
          placeholder={"warped text\nextra fingers\nplastic skin"}
        />

        <Textarea
          label="Render params (JSON)"
          hint={'A flat JSON object of scalar values, e.g. {"guidance": 3.5, "style": "realistic_image"}. Merged as the base layer; the engine keys (geometry, seed) always win.'}
          value={paramsText}
          onChange={(e) => setParamsText(e.target.value)}
          rows={5}
          className="font-mono text-xs"
          placeholder={'{\n  "guidance": 3.5,\n  "style": "realistic_image"\n}'}
        />

        {error && <p className="text-sm text-danger">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={onSaveClick} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save image settings"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Health tab: attempt log + per-provider breakdown ───────────────────────
function HealthTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "provider-attempts"],
    queryFn: () => adminApi.providerAttempts(),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const summary = data?.summary ?? {};
  const attempts = data?.attempts ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Per-provider (recent)</CardTitle></CardHeader>
        <CardContent className="flex flex-col gap-2">
          {Object.keys(summary).length === 0 ? (
            <p className="text-fgMuted">No attempts recorded yet.</p>
          ) : (
            Object.entries(summary).map(([mod, s]) => {
              const rate = s.total ? Math.round((s.success / s.total) * 100) : 0;
              return (
                <div key={mod} className="flex items-center gap-3 rounded-lg border border-border p-3">
                  <Badge variant="muted">{mod}</Badge>
                  <span className="text-sm text-fg">{rate}% success</span>
                  <span className="text-sm text-fgMuted">({s.success}/{s.total})</span>
                  <div className="flex gap-1">
                    {Object.entries(s.categories).map(([cat, n]) => (
                      <Badge key={cat} variant="danger">{cat}: {n}</Badge>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent attempts</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col gap-1 text-sm">
            {attempts.slice(0, 50).map((a) => (
              <div key={a.id} className="flex items-center gap-3 border-b border-border py-1.5">
                {a.success ? <CheckCircle2 className="h-4 w-4 text-success" /> : <XCircle className="h-4 w-4 text-danger" />}
                <span className="font-mono text-xs text-fgMuted">{new Date(a.createdAt).toLocaleTimeString()}</span>
                <Badge variant="muted">{a.moduleId}</Badge>
                <span className="text-fgMuted">{a.tier} · {a.modelSlug}</span>
                {a.category && <Badge variant="danger">{a.category}</Badge>}
                {a.latencyMs != null && <span className="text-fgMuted">{a.latencyMs}ms</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Balances & Spend tab: Layer A (spend) + B (credit alerts) + C ────────────
// (manual balances + runway). (tf3-25) The spend is MEASURED — the model.call spans' cents, as Operations → Spend reads
// them (tf3-24). The runway is a projection: there is no live balance API for these providers, so the copy says so.
function usd(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function BalancesTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ProviderBalance | null>(null);

  const { data: balData, isLoading: balLoading, error: balError } = useQuery({
    queryKey: ["admin", "provider-balances"],
    queryFn: () => adminApi.providerBalances(7),
  });
  const { data: alertData } = useQuery({
    queryKey: ["admin", "provider-alerts"],
    queryFn: () => adminApi.providerAlerts(24),
  });

  if (balLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const balances = balData?.balances ?? [];
  const alerts = alertData?.alerts ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* Layer B — active credit alerts */}
      {alerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-danger" /> Credit alerts (last 24h)
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {alerts.map((a) => (
              <div key={a.moduleId} className="flex items-center gap-3 rounded-lg border border-danger/40 bg-danger/5 p-3">
                <Badge variant="danger">{a.severity === "confirmed" ? "OUT OF CREDIT" : "SUSPECTED"}</Badge>
                <span className="text-sm font-medium text-fg">{a.moduleId}</span>
                <span className="text-sm text-fgMuted">
                  {a.count} failure{a.count === 1 ? "" : "s"} · last {new Date(a.lastSeen).toLocaleString()}
                </span>
                {a.severity === "suspected" && (
                  <span className="text-xs text-fgMuted">(auth failures — may be a bad key rather than empty balance)</span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Layer A + C — per-provider balance / runway / 7-day spend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Balances &amp; runway</CardTitle>
          <p className="text-xs text-fgMuted mt-1">
            The 7-day spend is <strong>measured</strong>: what each provider&apos;s calls cost, from the trail (the same
            figures as Operations → Spend). Runway is a <strong>projection</strong> from that spend and the balance you
            record — no provider exposes a live balance. Record what you see in each provider&apos;s console to project runway.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {/* (audit 10-15) the panel never reads as empty, or as $0 spent, when a read failed */}
          {balError && (
            <p className="text-sm text-danger" role="alert">The balances couldn&apos;t be read: {(balError as { message?: string }).message}</p>
          )}
          {balData?.spendError && (
            <p className="text-sm text-danger" role="alert">
              The 7-day spend couldn&apos;t be read ({balData.spendError}), so spend and runway show &quot;—&quot;. The balances below are as recorded.
            </p>
          )}
          {balances.map((b) => (
            <div
              key={b.moduleId}
              className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 ${
                b.warn ? "border-danger/50 bg-danger/5" : "border-border"
              }`}
            >
              <span className="text-sm font-medium text-fg min-w-[8rem]">{b.displayName}</span>
              {b.warn && <Badge variant="danger">LOW RUNWAY</Badge>}
              {b.needsBalance && <Badge variant="muted">no balance recorded</Badge>}

              <span className="text-sm text-fgMuted">
                Balance: <span className="text-fg">{usd(b.balanceUsdCents)}</span>
              </span>
              <span className="text-sm text-fgMuted">
                {/* (tf3-24) measured from the model.call spans, as Operations → Spend reads them — no longer an estimate */}
                7-day spend: <span className="text-fg">{usd(b.estimatedWindowCents)}</span>
              </span>
              <span className="text-sm text-fgMuted">
                Runway:{" "}
                <span className="text-fg">
                  {b.runwayDays == null ? "—" : `~${b.runwayDays} day${b.runwayDays === 1 ? "" : "s"}`}
                </span>
              </span>
              {b.autoReloadEnabled === false && (
                <Badge variant="muted" title="Enable auto-reload in the provider console so an unattended farm doesn't stall">
                  auto-reload OFF
                </Badge>
              )}

              <div className="ml-auto flex items-center gap-2">
                {b.balanceHint && b.balanceHint.startsWith("http") && (
                  <a
                    href={b.balanceHint}
                    target="_blank"
                    rel="noreferrer"
                    className="text-fgMuted hover:text-fg"
                    title="Open provider billing console"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <Button variant="secondary" size="sm" onClick={() => setEditing(b)}>
                  {b.hasBalance ? "Update" : "Record"} balance
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {editing && (
        <BalanceModal
          balance={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ["admin", "provider-balances"] });
          }}
        />
      )}
    </div>
  );
}

function BalanceModal({
  balance,
  onClose,
  onSaved,
}: {
  balance: ProviderBalance;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [dollars, setDollars] = useState(
    balance.balanceUsdCents != null ? (balance.balanceUsdCents / 100).toFixed(2) : "",
  );
  const [threshold, setThreshold] = useState(
    balance.lowThresholdUsdCents != null ? (balance.lowThresholdUsdCents / 100).toFixed(2) : "",
  );
  const [autoReload, setAutoReload] = useState(balance.autoReloadEnabled ?? false);
  const [note, setNote] = useState(balance.note ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const balanceUsdCents = Math.round(parseFloat(dollars) * 100);
      if (!Number.isFinite(balanceUsdCents) || balanceUsdCents < 0) {
        throw new Error("Enter a valid balance in dollars");
      }
      const lowThresholdUsdCents =
        threshold.trim() === "" ? null : Math.round(parseFloat(threshold) * 100);
      return adminApi.updateProviderBalance(balance.moduleId, {
        balanceUsdCents,
        lowThresholdUsdCents,
        autoReloadEnabled: autoReload,
        note: note.trim() === "" ? null : note,
      });
    },
    onSuccess: () => {
      toast.success("Balance recorded");
      onSaved();
    },
    onError: (e: unknown) => setError((e as Error).message),
  });

  return (
    <Modal open onClose={onClose} title={`${balance.displayName} — balance`}>
      <div className="flex flex-col gap-4">
        <p className="text-xs text-fgMuted">
          Enter the balance you see in the provider&apos;s console. This is a manual record — it doesn&apos;t read the
          provider live.
        </p>
        <div className="flex flex-col gap-1">
          <label className="text-sm text-fgMuted">Current balance (USD)</label>
          <Input value={dollars} onChange={(e) => setDollars(e.target.value)} placeholder="50.00" inputMode="decimal" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm text-fgMuted">Low-water threshold (USD, optional)</label>
          <Input value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="10.00" inputMode="decimal" />
          <span className="text-xs text-fgMuted">Warn when the projected balance falls below this.</span>
        </div>
        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" checked={autoReload} onChange={(e) => setAutoReload(e.target.checked)} />
          Auto-reload is enabled in the provider console
        </label>
        <div className="flex flex-col gap-1">
          <label className="text-sm text-fgMuted">Note (optional)</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Topped up $50 on 8/30…" />
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { setError(null); save.mutate(); }} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
